import React, { useCallback } from "react";
import { MarketButton } from "../../../../MarketButton/MarketButton";
import { Grid } from "components/src/components/Grid/Grid";
import { formatOdds } from "../js/formatOdds";
import { createMetaData } from "../js/createMetaData";
import { type MarketButtonType } from "components-sbk/src/components/MarketButton/types";
import { type MarketProps } from "../types";
import { type MarketTemplateTypes } from "../../../types";

interface MappedSelectionProps {
  selection: MarketButtonType;
  index: number;
  isInactive?: boolean;
  isMuted?: boolean;
  hasMultipleCompetitors?: boolean;
  onInactiveClick?: null | ((args: any) => void);
  marketTemplateType?: MarketTemplateTypes;
  marketButtonData: Pick<
    MarketProps,
    | "type"
    | "onSelectionClick"
    | "updateMarket"
    | "name"
    | "marketTemplateTitle"
    | "oddsFormat"
    | "buttonOnly"
    | "dataFilterGroup"
    | "sport"
    | "canSelectMarkets"
    | "selectedSelectionIds"
    | "marketHeader"
    | "competitor"
    | "competitors"
    | "isSelectedBuilder"
    | "priceDisplayFormatter"
    | "isOddsBoost"
  > & { isScrollable: boolean };
}

export const MappedSelection: React.FC<MappedSelectionProps> = ({
  selection,
  index,
  hasMultipleCompetitors,
  marketButtonData,
  marketTemplateType,
  isInactive: _isInactiveProp,
  isMuted = false,
  onInactiveClick,
}) => {
  const isInactive = _isInactiveProp ?? selection?.isInactive ?? false;
  const {
    type,
    onSelectionClick,
    updateMarket,
    name,
    marketTemplateTitle,
    oddsFormat,
    buttonOnly,
    dataFilterGroup,
    sport,
    canSelectMarkets,
    selectedSelectionIds,
    marketHeader,
    competitor,
    competitors,
    isScrollable,
    isOddsBoost,
    isSelectedBuilder,
    priceDisplayFormatter,
  } = marketButtonData;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
      if (
        canSelectMarkets ||
        (selectedSelectionIds && selectedSelectionIds.includes(selection.id!))
      ) {
        if (updateMarket)
          onSelectionClick?.({ e, id, selection, updateMarket });
      }
    },
    [selection, updateMarket, selectedSelectionIds, canSelectMarkets]
  );
  const odds = formatOdds(selection?.odds, oddsFormat);

  // Compose MarketButton metadata information
  const metadata = createMetaData({
    competitor,
    competitors,
    marketHeader,
    sport,
    name,
    i: index,
    marketTemplateTitle,
  });

  if (type === "rowgrouped") {
    return (
      <MarketButton
        truncateDescription={true}
        {...selection}
        filterGroup={dataFilterGroup}
        odds={odds}
        metadata={metadata}
        fluid={true}
        description={
          buttonOnly && name && !selection.description
            ? name
            : selection.description
        }
        isInactive={isInactive}
        isMuted={isMuted}
        onClick={handleClick}
        isSelectedBuilder={isSelectedBuilder}
        priceDisplayFormatter={priceDisplayFormatter}
        isDisabled={
          selection.isDisabled ||
          (!canSelectMarkets && !selectedSelectionIds?.includes(selection.id))
        }
        animation={"animation" in selection ? selection.animation : "reduced"}
      />
    );
  }

  if ((type === "row" || type === "scrollable") && isScrollable) {
    return (
      <div
        className="cui-w-14 cui-h-full"
        key={"id" in selection ? `msw-${selection.id}` : `msw-${index}`}
      >
        <MarketButton
          {...selection}
          odds={odds}
          fluid={true}
          description={
            buttonOnly && name && !selection.description
              ? name
              : selection.description
          }
          metadata={metadata}
          onClick={
            isInactive && typeof onInactiveClick === "function"
              ? onInactiveClick
              : handleClick
          }
          isInactive={isInactive}
          isMuted={isMuted}
          isSelectedBuilder={isSelectedBuilder}
          priceDisplayFormatter={priceDisplayFormatter}
          animation={"animation" in selection ? selection.animation : "reduced"}
        />
      </div>
    );
  }
  if (type === "row") {
    const showInlineMarketHeaderLabels =
      hasMultipleCompetitors &&
      marketTemplateType === "sixpack";

    const _label = marketHeader?.labels && typeof index === "number" && marketHeader.labels[index];

    return (
      <>
      { isOddsBoost ? <Grid.Cell colSpan={1} /> : null}
      <Grid.Cell
        colSpan={isOddsBoost ? 3 : 2}
        key={"id" in selection ? `msw-${selection.id}` : `msw-${index}`}
        items={hasMultipleCompetitors ? undefined : "center"}
        className={`${hasMultipleCompetitors ? "cui-block cui-mt-1" : ""}`}
      >
        {showInlineMarketHeaderLabels && _label ? (
          <div className="heading-xs cui-text-fg-moderate cui-w-full cui-truncate cui-text-center cui-mb-2xs">
            {typeof _label === "string" ? _label : _label.text}
          </div>
        ) : null}
        <MarketButton
          {...selection}
          odds={odds}
          fluid={true}
          description={
            buttonOnly && name && !selection.description
              ? name
              : selection.description
          }
          metadata={metadata}
          onClick={handleClick}
          isInactive={isInactive}
          isMuted={isMuted}
          variant={isOddsBoost ? "boost" : "fluid"}
          isSelectedBuilder={isSelectedBuilder}
          priceDisplayFormatter={priceDisplayFormatter}
          animation={"animation" in selection ? selection.animation : "reduced"}
        />
      </Grid.Cell>
    </>
    );
  }

  return (
    <Grid.Cell
      colSpan={2}
      key={"id" in selection ? `msw-${selection.id}` : `msw-${index}`}
      data-filter-group={dataFilterGroup}
    >
      <MarketButton
        {...selection}
        odds={odds}
        metadata={metadata}
        fluid={true}
        isInactive={isInactive}
        isMuted={isMuted}
        description={
          buttonOnly && name && !selection.description
            ? name
            : selection.description
        }
        onClick={handleClick}
        isDisabled={
          selection.isDisabled ||
          (!canSelectMarkets && !selectedSelectionIds?.includes(selection.id))
        }
        animation={"animation" in selection ? selection.animation : "reduced"}
      />
    </Grid.Cell>
  );
};

export default MappedSelection;
